import type { AdUnitDefinition, PrebidJS, Bid } from 'prebid.js'

import { AdType } from '@/modules/adv/types'
import type { AdProviderTypes, PlatformType } from '@/types'
import type { StartRumEvent } from '@/types/logger.types'
import { timer } from '@/utils/helpers'

import { AdConsoleLogger } from '../utils/ad-console-logger'
import { createRumTracker } from '../utils/create-rum-tracker'
import { isOutOfPageAdType } from '../utils/is-out-of-page-ad-type'
import { AuctionCache, type AuctionCacheContext } from './auction-cache'
import { BIDDER_TIMEOUT, getPrebidConfig } from './config'

const AUCTION_TIMEOUT_MS = 5_000

// Silent-failure watchdog:  bid that fires `bidWon` but never
// `adRenderSucceeded` within this window is treated as a silent render failure (white screen) and alerted on
const RENDER_WATCH_TIMEOUT_MS = 3_000
import { setRenderOptions } from './render-options-registry'
import type {
    ActiveCmps,
    AdAuctionSource,
    HbAdUnitDefinition,
    HbBidderConfig,
    HbBidderContext,
    HeaderBiddingAuctionResult,
    HeaderBiddingInterface,
    HeaderBiddingOptions,
    PendingHbAuction,
    RenderAdOptions,
} from './types'
import { hasValidVastMarkup } from './validate-video-bid'

export type { HbBidderConfig, HbAdUnitDefinition }

function resolveBids({
    bids,
    type,
    platform,
    adSource,
    hasConsent,
}: {
    bids: HbBidderConfig[]
    type: AdType
    platform: PlatformType
    adSource: HeaderBiddingOptions['adSource']
    hasConsent: boolean
}): NonNullable<AdUnitDefinition['bids']> {
    const context: HbBidderContext = { type, platform, adSource }
    const resolved = bids.map((bid) => ({
        ...bid,
        params: typeof bid.params === 'function' ? bid.params(context) : bid.params,
        ortb2Imp: bid.ortb2Imp,
    }))

    return resolved.filter(
        (bid): bid is (typeof resolved)[number] & { params: Record<string, unknown> } =>
            bid.params !== undefined && (hasConsent || bid.bidder === 'playgama'),
    )
}

// The Prebid Universal Creative posts `{ message: 'Prebid Request', adId }` (a JSON string) to the
// top window to fetch its markup. Returns the adId if `data` is such a request, else undefined.
function parsePrebidRequestAdId(data: unknown): string | undefined {
    if (typeof data !== 'string' || !data.includes('Prebid Request')) {
        return undefined
    }
    try {
        const parsed = JSON.parse(data)
        return parsed?.message === 'Prebid Request' && typeof parsed.adId === 'string' ? parsed.adId : undefined
    } catch {
        return undefined
    }
}

export class HeaderBidding implements HeaderBiddingInterface {
    private prebid: PrebidJS | undefined

    private readonly auctions = new Map<string, HeaderBiddingAuctionResult>()

    private readonly auctionCache = new AuctionCache(
        (adUnit, context) => this.runAuction(adUnit, context),
        (event) => this.options.logEvent(event),
    )

    private readonly auctionContexts = new Map<string, { type: AdType; provider: AdProviderTypes }>()

    // adId -> timeout handle for the bidWon → adRenderSucceeded silent-failure watchdog.
    private readonly pendingRenderWatches = new Map<string, ReturnType<typeof setTimeout>>()

    // adIds (among currently watched) whose GAM creative posted a "Prebid Request" back to pbjs.
    // Lets a silent failure distinguish "creative never asked" (postMessage never arrived) from
    // "creative asked but render still failed downstream".
    private readonly creativeRequestedAdIds = new Set<string>()

    private currentTargeting: Record<string, string | string[]> = {}

    private readonly adConsoleLogger = new AdConsoleLogger('hb')

    ready: Promise<void>

    private readonly hasConsent: boolean

    get adSource(): AdAuctionSource {
        return this.options.adSource
    }

    constructor(
        private options: HeaderBiddingOptions,
        activeCmps: ActiveCmps,
        private readonly startRumEvent?: StartRumEvent,
    ) {
        this.hasConsent = activeCmps.gpp || activeCmps.tcf
        const { resolve, promise } = Promise.withResolvers<void>()
        this.ready = promise

        this.adConsoleLogger.debug('start header bidding init')
        import('./prebidjs').then(({ prebid }) => {
            this.prebid = prebid

            this.prebid.setConfig(getPrebidConfig(this.options, activeCmps))
            this.applyTargeting()

            this.prebid.onEvent('bidRequested', (bidderRequest) => {
                const adUnitCode = bidderRequest.bids[0]?.adUnitCode
                const ctx = (adUnitCode && this.auctionContexts.get(adUnitCode)) || undefined
                this.options.logEvent({
                    event: 'custom_event',
                    eventName: 'ad_hb_bidder_request',
                    adPlacement: ctx?.type,
                    adProvider: ctx?.provider,
                    adUnit: adUnitCode,
                    adHbBidder: bidderRequest.bidderCode ?? undefined,
                })
            })

            // Arm the silent-failure watchdog when a fullscreen bid wins the ad server render.
            this.prebid.onEvent('bidWon', (wonBid) => {
                this.armSilentRenderFailureWatch(wonBid as Bid)
            })

            this.prebid.onEvent('adRenderSucceeded', ({ bid }) => {
                this.clearSilentRenderFailureWatch(bid.adId)
                const auctionResult = bid.auctionId && this.auctions.get(bid.auctionId)
                if (auctionResult) {
                    auctionResult.renderedBid = bid
                }
            })

            // Catches render-time failures Prebid hits on its own (e.g. a syntactically valid
            // VAST pointing at a dead video file, a renderer throwing) - failures the upfront
            // hasValidVastMarkup check can't predict from the markup string alone.
            this.prebid.onEvent('adRenderFailed', ({ bid, reason, message }) => {
                // A reported failure is not "silent" — disarm the watchdog so it doesn't double-alert.
                this.clearSilentRenderFailureWatch(bid?.adId)
                this.adConsoleLogger.error('Ad render failed', { reason, message, bidder: bid?.bidder })
                // adRenderFailed fires at render time, long after runAuction cleared the auctionContexts
                // entry — so read the context off the persisted auction result (keyed by auctionId),
                // falling back to the live auctionContexts map for still-in-flight auctions.
                const ctx = this.getBidContext(bid?.auctionId, bid?.adUnitCode)
                this.options.logEvent({
                    event: 'custom_event',
                    eventName: 'ad_hb_render_failed',
                    adUnit: bid?.adUnitCode,
                    adPlacement: ctx?.type,
                    adProvider: ctx?.provider,
                    adHbBidder: bid?.bidder,
                    dimension3: `${reason}: ${message}`,
                })
            })

            window.addEventListener('message', (e) => {
                try {
                    const { pgSSP } = e.data as { pgSSP: { event: string; payload: Record<string, unknown> } }
                    if (pgSSP && pgSSP.event === 'gtm') {
                        window.dataLayer.push(pgSSP.payload)
                    }
                } catch {}
            })

            // Track the GAM creative → pbjs render handshake. The Prebid Universal Creative posts a
            // "Prebid Request" (with its adId) to fetch the markup; if it never arrives for a bid we're
            // watching, the creative couldn't reach pbjs (the postMessage-lost theory).
            window.addEventListener('message', (e) => {
                const adId = parsePrebidRequestAdId(e.data)
                if (!adId) {
                    return
                }
                if (this.pendingRenderWatches.has(adId)) {
                    this.creativeRequestedAdIds.add(adId)
                }
                this.adConsoleLogger.debug('creative posted "Prebid Request"', { adId, origin: e.origin })
            })

            this.adConsoleLogger.debug('header bidding init completed')
            resolve()
        })
    }

    async getUserSignals() {
        this.adConsoleLogger.debug('start getUserSignals')

        const data = await this.prebid?.getUserIdsAsync()
        this.adConsoleLogger.debug('getUserSignals completed')

        const modules = data ? Object.keys(data).filter((k) => data[k] !== null && data[k] !== undefined) : []
        const modulesStr = modules.join(',') || 'none'

        this.adConsoleLogger.debug(`user ID modules loaded: ${modulesStr}`)
        this.options.logEvent({
            event: 'custom_event',
            eventName: 'ad_hb_user_ids',
            dimension1: modulesStr,
        })

        return data
    }

    public updateTargeting(targeting: Record<string, string | string[]> = {}): void {
        this.currentTargeting = { ...targeting }
        this.applyTargeting()
    }

    getOrRunAuction(
        getAdUnit: () => HbAdUnitDefinition | undefined,
        context: AuctionCacheContext,
    ): PendingHbAuction | undefined {
        return this.auctionCache.takeOrRun(getAdUnit, context)
    }

    releaseAuction(pending: PendingHbAuction, context: AuctionCacheContext): void {
        this.auctionCache.put(pending, context)
    }

    resetAuctionCache(): void {
        this.auctionCache.clear()
    }

    markAuctionResultUsed(auctionResult: HeaderBiddingAuctionResult | undefined): void {
        const bid = auctionResult?.renderedBid
        // banner bids are marked `rendered` and billed by prebid's renderAd itself; video plays
        // through IMA bypassing prebid, so it must be marked (and billed) manually
        if (bid && bid.status !== 'rendered') {
            this.prebid?.markWinningBidAsUsed({ adId: bid.adId, events: true })
        }
    }

    async runAuction(
        adUnit: HbAdUnitDefinition,
        context: { type: AdType; provider: AdProviderTypes },
    ): Promise<HeaderBiddingAuctionResult | undefined> {
        if (!this.prebid) {
            return undefined
        }

        const resolvedAdUnit = {
            ...adUnit,
            bids: resolveBids({
                bids: adUnit.bids,
                type: context.type,
                platform: this.options.platform,
                adSource: this.options.adSource,
                hasConsent: this.hasConsent,
            }),
        }

        this.adConsoleLogger.debug(`running auction for ${context.type} (${context.provider})`)

        this.auctionContexts.set(resolvedAdUnit.code, context)
        const auctionTracker = this.startRumEvent ? createRumTracker('hb-auction', this.startRumEvent) : null

        const requestResult = await Promise.race([
            this.prebid.requestBids({ adUnits: [resolvedAdUnit], timeout: BIDDER_TIMEOUT }),
            timer(AUCTION_TIMEOUT_MS),
        ])
        this.auctionContexts.delete(resolvedAdUnit.code)

        if (!requestResult) {
            this.adConsoleLogger.error('Auction timed out (prebid did not resolve)', {
                adUnit: resolvedAdUnit.code,
                provider: context.provider,
            })
            auctionTracker?.send({
                status: 'error',
                dimension1: context.provider,
                dimension2: context.type,
                dimension3: 'timeout',
            })
            this.options.logEvent({
                event: 'custom_event',
                eventName: 'ad_hb_auction_timeout',
                adPlacement: context.type,
                adProvider: context.provider,
                adUnit: adUnit.code,
            })
            return undefined
        }

        const { auctionId } = requestResult

        const winningBid = this.prebid.getHighestCpmBids(resolvedAdUnit.code)?.[0]

        auctionTracker?.send({
            status: winningBid ? 'success' : 'empty',
            dimension1: context.provider,
            dimension2: context.type,
            dimension3: winningBid?.bidder ?? 'no-bidder',
        })
        const targeting = this.prebid.getAdserverTargetingForAdUnitCode(resolvedAdUnit.code)

        // Some bidders report mediaType: 'video' with no actual VAST markup (just a tracking
        // pixel/script) — rendering that as video fails outright, losing the placement. The
        // mediaType field itself can't be reassigned to 'banner' (Bid['mediaType'] only allows
        // 'video' | 'audio'), so consumers (e.g. HeaderBiddingAdProvider) check
        // hasValidVastMarkup themselves before treating a bid as video. We just log it here so we
        // can track how often it happens.
        if (winningBid?.mediaType === 'video' && !hasValidVastMarkup(winningBid.vastXml)) {
            this.adConsoleLogger.error('Invalid video bid (no VAST markup), will render as banner', {
                bidder: winningBid.bidder,
                adUnit: resolvedAdUnit.code,
            })
            this.options.logEvent({
                event: 'custom_event',
                eventName: 'ad_hb_invalid_format_bid',
                adPlacement: context.type,
                adProvider: context.provider,
                adUnit: adUnit.code,
                dimension3: `resulted winningBid vastXml: ${winningBid.vastXml?.slice(0, 150)}`,
                adHbBidder: winningBid.bidder,
            })
        }

        const auctionResult = this.createAuctionResult({ auctionId, targeting, winningBid, context })

        this.adConsoleLogger.debug(`auction result for ${context.type}`, {
            adUnit: adUnit.code,
            bidder: auctionResult.winningBid?.bidder ?? 'no-bidder',
            cpm: auctionResult.winningBid?.cpm,
            auctionId,
        })

        this.options.logEvent({
            event: 'custom_event',
            eventName: 'ad_hb_auction',
            adPlacement: context.type,
            adProvider: context.provider,
            adUnit: adUnit.code,
            adHbCpmUsd: auctionResult.winningBid?.cpm ?? undefined,
            adHbBidder: auctionResult.winningBid?.bidder || 'no-bidder',
            adHbRequestId: auctionResult.winningBid?.requestId || undefined,
            dimension1: auctionResult.winningBid?.mediaType,
        })

        this.prebid
            .getBidResponsesForAdUnitCode(resolvedAdUnit.code)
            .bids.filter((b) => b.auctionId === auctionId)
            .forEach((bid) => {
                this.options.logEvent({
                    event: 'custom_event',
                    eventName: 'ad_hb_bidder_response',
                    adPlacement: context.type,
                    adProvider: context.provider,
                    adUnit: adUnit.code,
                    adHbCpmUsd: bid.cpm,
                    adHbBidder: bid.bidder,
                    adHbRequestId: bid.requestId || undefined,
                    dimension1: String(bid.timeToRespond),
                })
            })

        return auctionResult
    }
    private getBidContext(auctionId?: string, adUnitCode?: string) {
        return (
            (auctionId && this.auctions.get(auctionId)?.context) ||
            (adUnitCode && this.auctionContexts.get(adUnitCode)) ||
            undefined
        )
    }

    // bidWon fired: for fullscreen (interstitial/rewarded) units, expect an adRenderSucceeded shortly
    // after. If it never arrives, the render silently failed (white screen) — alert on it.
    private armSilentRenderFailureWatch(bid: Bid): void {
        const { adId } = bid
        if (!adId) {
            return
        }

        // Video (instream) bids render through IMA, not pbjs.renderAd: `bidWon` fires (via
        // markWinningBidAsUsed({ events: true })) but `adRenderSucceeded` never does, so the watchdog
        // would flag every successfully played video. The silent white-screen failure is banner-only.
        if (bid.mediaType === 'video') {
            return
        }

        const ctx = this.getBidContext(bid.auctionId, bid.adUnitCode)
        // Only fullscreen out-of-page formats suffer the silent white-screen handshake failure.
        if (!ctx || !isOutOfPageAdType(ctx.type)) {
            return
        }

        this.clearSilentRenderFailureWatch(adId)

        const handle = setTimeout(() => {
            this.pendingRenderWatches.delete(adId)
            // Did the GAM creative even ask pbjs for its markup? Distinguishes the postMessage-lost
            // case (creative never reached pbjs) from a downstream render failure.
            const creativeRequested = this.creativeRequestedAdIds.delete(adId)
            this.adConsoleLogger.error('Silent render failure: bidWon without adRenderSucceeded', {
                adUnit: bid.adUnitCode,
                bidder: bid.bidder,
                adId,
                creativeRequested,
            })
            this.options.logEvent({
                event: 'custom_event',
                eventName: 'ad_hb_render_silent_failure',
                adUnit: bid.adUnitCode,
                adPlacement: ctx.type,
                adProvider: ctx.provider,
                adHbBidder: bid.bidder,
                adHbCpmUsd: bid.cpm ?? undefined,
                dimension3: `bidWon but no adRenderSucceeded within ${RENDER_WATCH_TIMEOUT_MS}ms; creativeRequested=${creativeRequested}`,
            })
        }, RENDER_WATCH_TIMEOUT_MS)

        this.pendingRenderWatches.set(adId, handle)
    }

    private clearSilentRenderFailureWatch(adId?: string): void {
        if (!adId) {
            return
        }
        this.creativeRequestedAdIds.delete(adId)
        const handle = this.pendingRenderWatches.get(adId)
        if (handle !== undefined) {
            clearTimeout(handle)
            this.pendingRenderWatches.delete(adId)
        }
    }

    private applyTargeting(): void {
        this.prebid?.setConfig({
            ortb2: {
                site: {
                    ext: {
                        data: { ...this.currentTargeting },
                    },
                },
            },
        })
    }

    private createAuctionResult({
        auctionId,
        targeting,
        winningBid,
        context,
    }: {
        auctionId?: string
        targeting?: Record<string, string | string[]>
        winningBid?: Bid
        context?: { type: AdType; provider: AdProviderTypes }
    }): HeaderBiddingAuctionResult {
        const auctionResult: HeaderBiddingAuctionResult = {
            auctionId,
            targeting,
            winningBid,
            timestamp: Date.now(),
            context,
        }

        if (auctionId) {
            this.auctions.set(auctionId, auctionResult)
        }

        return auctionResult
    }

    /**
     * Render the winning bid into the provided container element.
     * Banner/HTML creatives: sandboxed iframe + pbjs.renderAd.
     */
    renderAd(bid: Bid, container: HTMLElement, options?: RenderAdOptions): boolean {
        if (!this.prebid) {
            this.adConsoleLogger.error('Prebid not initialized')
            return false
        }

        // Some bidders report mediaType: 'video' with a renderer configured but no actual VAST
        // markup (just a tracking pixel/script) — handing that to the renderer fails outright.
        // Fall back to the plain banner iframe path below, using bid.ad, instead of losing the
        // placement.
        const isValidVideoBid =
            (bid.mediaType === 'video' && hasValidVastMarkup(bid.vastXml)) || bid.mediaType !== 'video'

        // Renderer bids (outstream video + h5 creatives): let Prebid drive the pipeline.
        // isRendererRequired checks for renderer.url or renderer.renderNow,
        // and executeRenderer calls renderer.render(bid) internally, which
        // also fires the adRenderSucceeded event.
        if (isValidVideoBid && (bid.renderer?.renderNow || bid.renderer?.url)) {
            try {
                // Clear stale content from a previous render so the renderer
                // always gets an empty container (critical for refresh: without
                // this the old outstream player blocks the new one).
                container.innerHTML = ''
                if (options) {
                    setRenderOptions(bid.adUnitCode, options)
                }
                pbjs.renderAd(document, bid.adId)
                return true
            } catch (error) {
                this.adConsoleLogger.error('Failed to render ad:', error)
                return false
            }
        }
        // Banner bids: write markup into a sandboxed iframe.
        try {
            if (!bid.ad) {
                throw new Error('No ad markup')
            }

            container.innerHTML = ''

            const iframe = document.createElement('iframe')
            iframe.width = String(bid.width)
            iframe.height = String(bid.height)
            iframe.style.width = `${bid.width}px`
            iframe.style.height = `${bid.height}px`
            iframe.style.border = '0'
            iframe.style.display = 'block'
            iframe.setAttribute('scrolling', 'no')
            container.appendChild(iframe)

            const frameDocument = iframe.contentWindow?.document
            if (!frameDocument) {
                throw new Error('Unable to access iframe document for direct render')
            }

            const adFrameTemplate = `
<!doctype html>
<html>
    <head>
     <script>
        window.addEventListener('message', function(event) {
            window.parent.postMessage(event.data, '*')
        })
    </script>
    </head>
    <body style="margin:0"></body>
</html>
`

            frameDocument.open()
            frameDocument.write(adFrameTemplate)
            frameDocument.close()

            pbjs.renderAd(frameDocument, bid.adId)

            return true
        } catch (error) {
            this.adConsoleLogger.error('Failed to render ad:', error)
            return false
        }
    }
}
